/**
 * Accesibilidad: filtros de color, marcas de equipo y tamaño de texto.
 *
 * **Por qué un FILTRO y no una paleta de equipos.** La primera versión resolvía
 * el daltonismo dejando elegir los colores de los dos equipos. Eso funcionaba
 * mientras los únicos colores del juego eran dos discos; deja de funcionar en
 * cuanto hay poleras por equipo, estelas de patada, explosiones de gol o un
 * mapa con marcas propias: el jugador tendría que ir eligiendo color de cada
 * cosa, una por una, y aun así el resto de la imagen seguiría sin corregir.
 *
 * Aquí el ajuste se aplica a la IMAGEN ENTERA, como el filtro de un televisor:
 *
 *  1. **Daltonización** (`ColorFilter`): una matriz que redistribuye la
 *     información del eje de color que la persona no percibe hacia los ejes que
 *     sí percibe. No "pinta de otro color"; conserva la escena y separa lo que
 *     antes se confundía. La intensidad es regulable porque la dicromacia no es
 *     un interruptor: mucha gente distingue algo y una corrección al 100 % le
 *     resulta chillona.
 *  2. **Contraste y saturación**: para pantallas malas, sol de frente y baja
 *     visión, que es un caso mucho más común que cualquier dicromacia.
 *  3. **Marcas de equipo** (anillo lleno contra anillo partido): lo único que
 *     sigue funcionando en escala de grises y sin depender de la percepción del
 *     color en absoluto. Por eso viene activado por defecto.
 *  4. **Reducir destellos**: la animación de gol es un fogonazo a pantalla
 *     completa. Para alguien con fotosensibilidad eso no es un adorno.
 *
 * El módulo es puro salvo los aplicadores del final, que escriben en el `:root`
 * del documento (y montan una vez las definiciones SVG de los filtros).
 */

/* ---------------- paleta de equipos (fija) ---------------- */

/**
 * Marca de forma del disco, independiente del color (ver cabecera).
 *
 * - `solid`: un anillo continuo pegado al borde.
 * - `double`: ese anillo MÁS otro concéntrico por dentro.
 *
 * Antes el segundo equipo llevaba un anillo PARTIDO en cuatro, y se leía como
 * un fallo de dibujo: "el borde del disco turquesa sale roto". Una marca de
 * accesibilidad que la gente reporta como bug no está haciendo su trabajo, por
 * bien que distinga en escala de grises. Dos anillos concéntricos se distinguen
 * igual de bien sin parecer nunca un error.
 */
export type TeamMarker = "solid" | "double";

export interface TeamStyle {
  /** Color de relleno del disco. */
  color: string;
  /** Marca de forma que acompaña al color ("none" = sin marca). */
  marker: TeamMarker | "none";
}

/**
 * Colores de marca de los dos equipos (rojo fuego · cian eléctrico).
 *
 * Son EXACTAMENTE el duotono del sistema de diseño (`--tb-magenta` y
 * `--tb-cyan`, ver `src/design/system.ts`): la interfaz y la cancha tienen que
 * hablar del mismo equipo con el mismo color, o el marcador y los discos dejan
 * de parecer parte del mismo juego. Si cambian aquí, cambian allí.
 */
export const TEAM_COLORS: [string, string] = ["#ff2e63", "#22e6ff"];

/** `"#ff2e63"` → `"255,46,99"`. Para meter el color dentro de un `rgba(…)`. */
export function toRgb(hex: string): string {
  const h = hex.replace("#", "");
  const n = parseInt(h.length === 3 ? h.split("").map((c) => c + c).join("") : h, 16);
  return `${(n >> 16) & 255},${(n >> 8) & 255},${n & 255}`;
}

/**
 * Los mismos dos colores, en el formato que necesitan los efectos del lienzo.
 *
 * Existe porque estaban escritos DOS VECES: `TEAM_COLORS` en hex aquí y
 * `"255,46,99"` a mano en el sistema de estelas. Dos copias de una paleta es una
 * paleta que se cambia a medias — se toca el hex, los discos y el marcador
 * cambian de color, y las estelas y las explosiones se quedan con el anterior
 * sin que nada falle.
 */
export const TEAM_RGB: [string, string] = [toRgb(TEAM_COLORS[0]), toRgb(TEAM_COLORS[1])];

/** Color del balón: contrasta con los dos equipos y con el campo. */
export const BALL_COLOR = "#f2f7fb";
/** El mismo, para meterlo en un `rgba(…)` (estela del balón). */
export const BALL_RGB = toRgb(BALL_COLOR);

/**
 * Estilos de los dos equipos. `markers` decide si el disco lleva además su
 * marca de forma; el equipo 0 lleva SIEMPRE el anillo simple y el 1 el doble,
 * para que la marca signifique lo mismo en toda la sesión.
 */
export function teamStyles(markers = true): [TeamStyle, TeamStyle] {
  return [
    { color: TEAM_COLORS[0], marker: markers ? "solid" : "none" },
    { color: TEAM_COLORS[1], marker: markers ? "double" : "none" },
  ];
}

/* ---------------- filtros de color ---------------- */

/** Tipo de daltonismo a corregir ("none" = sin corrección). */
export type ColorFilter = "none" | "deuteranopia" | "protanopia" | "tritanopia";

export const COLOR_FILTERS: readonly ColorFilter[] = [
  "none", "deuteranopia", "protanopia", "tritanopia",
];

export function isColorFilter(v: unknown): v is ColorFilter {
  return typeof v === "string" && (COLOR_FILTERS as readonly string[]).includes(v);
}

/** Matriz 3×3 fila-mayor (RGB → RGB). */
export type Matrix3 = readonly [
  number, number, number,
  number, number, number,
  number, number, number,
];

const IDENTITY: Matrix3 = [1, 0, 0, 0, 1, 0, 0, 0, 1];

/* --- simulación de la dicromacia, vía espacio LMS (Brettel/Viénot) --- */

/** RGB → LMS (respuesta de los tres tipos de cono). */
const RGB_TO_LMS: Matrix3 = [
  17.8824, 43.5161, 4.11935,
  3.45565, 27.1554, 3.86714,
  0.0299566, 0.184309, 1.46709,
];

/** LMS → RGB (la inversa de la anterior). */
const LMS_TO_RGB: Matrix3 = [
  0.0809444479, -0.130504409, 0.116721066,
  -0.0102485335, 0.0540193266, -0.113614708,
  -0.000365296938, -0.00412161469, 0.693511405,
];

/**
 * Proyección de cada dicromacia EN LMS: el cono que falta se reconstruye a
 * partir de los otros dos, que es exactamente lo que hace un ojo dicrómata.
 */
const LMS_PROJECT: Record<Exclude<ColorFilter, "none">, Matrix3> = {
  protanopia: [0, 2.02344, -2.52581, 0, 1, 0, 0, 0, 1],
  deuteranopia: [1, 0, 0, 0.494207, 0, 1.24827, 0, 0, 1],
  tritanopia: [1, 0, 0, 0, 1, 0, -0.395913, 0.801109, 0],
};

/**
 * Cómo se reparte el error de color hacia los canales que sí se distinguen
 * (daltonización clásica: el error del eje perdido se vuelca sobre verde y
 * azul, que son los que quedan disponibles en los dos casos comunes).
 */
const SHIFT: Matrix3 = [0, 0, 0, 0.7, 1, 0, 0.7, 0, 1];

function multiply(a: Matrix3, b: Matrix3): Matrix3 {
  const out = new Array<number>(9);
  for (let r = 0; r < 3; r++) {
    for (let c = 0; c < 3; c++) {
      out[r * 3 + c] = a[r * 3] * b[c] + a[r * 3 + 1] * b[3 + c] + a[r * 3 + 2] * b[6 + c];
    }
  }
  return out as unknown as Matrix3;
}

/** Mezcla una matriz con la identidad: `strength` 0 = sin efecto, 1 = completo. */
function blend(m: Matrix3, strength: number): Matrix3 {
  const s = Math.max(0, Math.min(1, strength));
  return m.map((v, i) => IDENTITY[i] + (v - IDENTITY[i]) * s) as unknown as Matrix3;
}

/**
 * Cómo VE una dicromacia: `LMS→RGB · proyección · RGB→LMS`.
 *
 * Se compone aquí en vez de copiar una matriz sRGB ya multiplicada de algún
 * sitio, porque la corrección de abajo se DERIVA de esta misma matriz: si las
 * dos no salen del mismo modelo, la "corrección" puede acabar juntando colores
 * en vez de separarlos (pasó: con matrices de dos fuentes distintas, el filtro
 * acercaba el rojo y el turquesa de los equipos).
 */
export function simulateMatrix(filter: Exclude<ColorFilter, "none">): Matrix3 {
  return multiply(LMS_TO_RGB, multiply(LMS_PROJECT[filter], RGB_TO_LMS));
}

/**
 * Matriz de daltonización: `C' = C + SHIFT · (C − Sim(C))`, es decir
 * `I + SHIFT · (I − Sim)`, mezclada con la identidad según la intensidad.
 *
 * Se calcula y no se copia una matriz mágica para que la intensidad sea un
 * parámetro real, y no cinco matrices distintas mantenidas a mano.
 */
export function daltonizeMatrix(filter: ColorFilter, strength = 1): Matrix3 {
  if (filter === "none") return IDENTITY;
  const sim = simulateMatrix(filter);
  const err = sim.map((v, i) => IDENTITY[i] - v) as unknown as Matrix3;
  const corr = multiply(SHIFT, err);
  const full = corr.map((v, i) => IDENTITY[i] + v) as unknown as Matrix3;
  return blend(full, strength);
}

/** La matriz 3×3 como los 20 valores `feColorMatrix` (4×5, RGBA). */
export function colorMatrixValues(m: Matrix3): number[] {
  return [
    m[0], m[1], m[2], 0, 0,
    m[3], m[4], m[5], 0, 0,
    m[6], m[7], m[8], 0, 0,
    0, 0, 0, 1, 0,
  ];
}

/* ---------------- rangos de los ajustes ---------------- */

export const MIN_UI_SCALE = 0.85;
export const MAX_UI_SCALE = 1.6;
export const DEFAULT_UI_SCALE = 1;

export const MIN_CONTRAST = 0.8;
export const MAX_CONTRAST = 1.6;
export const MIN_SATURATION = 0.4;
export const MAX_SATURATION = 2;

/**
 * Acota un número a un rango, con valor por defecto si no es un número real.
 *
 * Se exige un número finito de verdad en vez de convertir lo que llegue: la
 * coerción de JavaScript convierte `null` y `""` en 0, y 0 acotado es el
 * MÍNIMO — así que un ajuste corrupto no dejaba el valor por defecto, dejaba al
 * jugador con el texto más pequeño posible y sin pista de por qué.
 */
export function clampNumber(v: unknown, min: number, max: number, fallback: number): number {
  if (typeof v !== "number" || !Number.isFinite(v)) return fallback;
  return Math.max(min, Math.min(max, v));
}

export function clampUiScale(v: unknown): number {
  return clampNumber(v, MIN_UI_SCALE, MAX_UI_SCALE, DEFAULT_UI_SCALE);
}

export function clampStrength(v: unknown): number {
  return clampNumber(v, 0, 1, 1);
}

export function clampContrast(v: unknown): number {
  return clampNumber(v, MIN_CONTRAST, MAX_CONTRAST, 1);
}

export function clampSaturation(v: unknown): number {
  return clampNumber(v, MIN_SATURATION, MAX_SATURATION, 1);
}

/* ---------------- aplicadores (tocan el documento) ---------------- */

/** Ajustes visuales que se aplican al documento entero. */
export interface VisionSettings {
  colorFilter: ColorFilter;
  filterStrength: number;
  contrast: number;
  saturation: number;
}

/**
 * Cadena CSS del filtro, o `"none"` si no hay nada que corregir.
 *
 * El orden importa: primero la daltonización (que trabaja sobre los colores
 * originales) y después contraste/saturación, que son retoques de presentación.
 */
export function filterCss(v: VisionSettings, filterId = FILTER_ID): string {
  const parts: string[] = [];
  if (v.colorFilter !== "none" && v.filterStrength > 0) parts.push(`url(#${filterId})`);
  const contrast = clampContrast(v.contrast);
  const saturation = clampSaturation(v.saturation);
  if (Math.abs(contrast - 1) > 0.001) parts.push(`contrast(${contrast.toFixed(2)})`);
  if (Math.abs(saturation - 1) > 0.001) parts.push(`saturate(${saturation.toFixed(2)})`);
  return parts.length ? parts.join(" ") : "none";
}

/** Id del `<filter>` SVG que lleva la matriz de daltonización. */
export const FILTER_ID = "tak-cvd";
const SVG_HOST_ID = "tak-a11y-svg";
const STYLE_ID = "tak-a11y-style";

/**
 * Regla que aplica el filtro. Va sobre los HIJOS DIRECTOS de `body` (el canvas
 * del juego y cada overlay de la interfaz, que es como está montado todo) en
 * vez de sobre `body` o `html`: un `filter` sobre un ancestro convierte a ese
 * ancestro en el bloque contenedor de sus descendientes `position: fixed`, y
 * toda la interfaz de este juego es fija a pantalla completa — se descuadraría
 * entera. Aplicándolo hoja por hoja, cada capa se corrige por su cuenta y la
 * maquetación no se entera.
 */
const FILTER_STYLE = `
:root[data-a11y-filter="on"] body > * { filter: var(--v-a11y-filter, none); }
`;

/** Monta (una vez) las definiciones SVG del filtro y su hoja de estilo. */
function ensureFilterHost(doc: Document): SVGFEColorMatrixElement | null {
  if (!doc.getElementById(STYLE_ID)) {
    const style = doc.createElement("style");
    style.id = STYLE_ID;
    style.textContent = FILTER_STYLE;
    doc.head.appendChild(style);
  }
  let host = doc.getElementById(SVG_HOST_ID) as unknown as SVGSVGElement | null;
  if (!host) {
    const NS = "http://www.w3.org/2000/svg";
    host = doc.createElementNS(NS, "svg");
    host.id = SVG_HOST_ID;
    // El SVG solo existe para alojar el <filter>: no debe ocupar sitio ni
    // capturar toques, y `display:none` lo dejaría inservible en algunos
    // motores, así que se esconde por tamaño.
    host.setAttribute("width", "0");
    host.setAttribute("height", "0");
    host.setAttribute("aria-hidden", "true");
    host.style.cssText = "position:absolute;width:0;height:0;pointer-events:none;";
    const filter = doc.createElementNS(NS, "filter");
    filter.id = FILTER_ID;
    // Sin esto el filtro trabaja en sRGB linealizado y los colores del juego
    // salen lavados: se pide el mismo espacio en el que están definidos.
    filter.setAttribute("color-interpolation-filters", "sRGB");
    const matrix = doc.createElementNS(NS, "feColorMatrix");
    matrix.setAttribute("type", "matrix");
    matrix.setAttribute("values", colorMatrixValues(IDENTITY).join(" "));
    filter.appendChild(matrix);
    host.appendChild(filter);
    doc.body.appendChild(host);
  }
  return host.querySelector("feColorMatrix");
}

/**
 * Aplica los ajustes de visión al documento: matriz de daltonización, filtro
 * CSS (contraste/saturación incluidos) y bandera para el CSS.
 */
export function applyVision(v: VisionSettings, doc: Document = document): void {
  if (typeof doc === "undefined" || !doc.body) return;
  const matrix = ensureFilterHost(doc);
  if (matrix) {
    const m = daltonizeMatrix(v.colorFilter, clampStrength(v.filterStrength));
    matrix.setAttribute("values", colorMatrixValues(m).join(" "));
  }
  const css = filterCss(v);
  // Sin corrección se publica `opacity(1)` (filtro identidad) en vez de `none`:
  // otras reglas COMPONEN sobre esta variable (el desenfoque del fondo del menú
  // hace `filter: var(--v-a11y-filter) blur(...)`), y un `none` en medio de una
  // lista de filtros invalida la declaración entera.
  doc.documentElement.style.setProperty("--v-a11y-filter", css === "none" ? "opacity(1)" : css);
  // El atributo enciende la regla: sin filtro no se toca el `filter` de nada,
  // y así el caso normal no paga ni una capa de composición de más.
  if (css === "none") doc.documentElement.removeAttribute("data-a11y-filter");
  else doc.documentElement.setAttribute("data-a11y-filter", "on");
}

/**
 * Aplica la escala de texto como variable CSS en `:root`.
 *
 * Va por variable y no tocando el `font-size` del `html` a propósito: la UI usa
 * píxeles en muchos sitios (paneles con alto fijo, chips del roster) y reescalar
 * la raíz los descuadraría. Con `--v-scale` cada componente decide qué crece.
 */
export function applyUiScale(scale: number, doc: Document = document): void {
  doc.documentElement.style.setProperty("--v-scale", String(clampUiScale(scale)));
}
